import React, { useEffect, useState } from "react";
import { createMember, updateMember } from "../../services/auth";

const toLocalDate = (dateStr) => {
  const date = new Date(dateStr);
  const offset = date.getTimezoneOffset();
  date.setMinutes(date.getMinutes() - offset);
  return date.toISOString().split("T")[0];
};

const MemberDetailForm = ({
  mainUserId,
  user,
  onClose,
  members,
  setMembers,
}) => {
  const [formErrors, setFormErrors] = useState({});
  const [formData, setFormData] = useState({
    uId: mainUserId,
    ID: "",
    Rel: "",
    Naam: "",
    bDate: "",
    MrdStatus: "",
    Edu: "",
    Prof: "",
    Blood: "",
    gen: "",
  });

  // Dropdown options
  const genderOptions = [
    { label: "સ્ત્રી", value: "0" },
    { label: "પુરુષ", value: "1" },
  ];

  const bloodGroupOptions = [
    { value: "A+", label: "A+" },
    { value: "A-", label: "A-" },
    { value: "B+", label: "B+" },
    { value: "B-", label: "B-" },
    { value: "AB+", label: "AB+" },
    { value: "AB-", label: "AB-" },
    { value: "O+", label: "O+" },
    { value: "O-", label: "O-" },
  ];

  const maritalStatusOptions = [
    { label: "અપરિણીત", value: "0" },
    { label: "પરિણિત", value: "1" },
  ];

  useEffect(() => {
    if (user) {
      setFormData({
        ID: user.ID || "",
        uId: mainUserId || "",
        Rel: user.Rel || "",
        Naam: user.Naam || "",
        bDate: user.bDate ? toLocalDate(user.bDate) : "",
        MrdStatus: user.MrdStatus?.toString() || "",
        Edu: user.Edu || "",
        Prof: user.Prof || "",
        Blood: user.Blood?.toString() || "",
        Blood: user.Blood || "",
        gen: user.gen?.toString() || "",
      });
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleDropdownChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    let errors = {};
    if (!formData.Rel) errors.Rel = "Relation is required";
    if (!formData.Naam?.trim()) errors.Naam = "Name is required";
    if (!formData.gen) errors.gen = "Gender is required";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (validateForm()) {
      try {
        const dataToSave = { ...formData };
        let result;
        if (dataToSave.ID) {
          result = await updateMember(dataToSave);
          // Update existing member in the members array
          setMembers((prevMembers) =>
            prevMembers.map((member) =>
              member.ID === dataToSave.ID
                ? { ...member, ...result.data }
                : member
            )
          );
        } else {
          result = await createMember(dataToSave);
          // Add new member to the members array
          setMembers((prevMembers) => [...prevMembers, result.data]);
        }
        alert("Member details saved successfully!");
        onClose();
      } catch (error) {
        console.error("Error saving Member details:", error);
        setFormErrors({
          _general: "Failed to save Member details. Please try again.",
        });
      }
    } else {
      console.log("Validation failed", formErrors);
    }
  };

  const getInputClassName = (fieldName) => {
    return `shadow appearance-none border ${
      formErrors[fieldName] ? "border-red-500" : "border-gray-300"
    } rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500`;
  };

  return (
    <div className="max-w-6xl w-full mx-auto p-6 md:p-4 bg-white shadow-xl rounded-xl">
      <h5 className="text-3xl md:text-2xl font-semibold text-gray-800 text-center mb-8">
        Member Detail Form
      </h5>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
        {[
          { label: "Relation", name: "Rel" },
          { label: "Name", name: "Naam" },
          { label: "Birth Date", name: "bDate", type: "date" },
          { label: "Education", name: "Edu" },
          { label: "Profession", name: "Prof" },
        ].map(({ label, name, type = "text" }) => (
          <div key={name}>
            <label
              htmlFor={name}
              className="block text-sm font-medium text-gray-700"
            >
              {label} <span className="text-red-500">*</span>
            </label>
            <input
              type={type}
              id={name}
              name={name}
              value={formData[name]}
              onChange={handleInputChange}
              className={getInputClassName(name)}
            />
            {formErrors[name] && (
              <p className="text-red-500 text-xs italic mt-1">
                {formErrors[name]}
              </p>
            )}
          </div>
        ))}

        {/* Marital Status Dropdown */}
        <div>
          <label
            htmlFor="MrdStatus"
            className="block text-sm font-medium text-gray-700"
          >
            Marital Status <span className="text-red-500">*</span>
          </label>
          <select
            id="MrdStatus"
            name="MrdStatus"
            value={formData.MrdStatus}
            onChange={(e) => handleDropdownChange("MrdStatus", e.target.value)}
            className={getInputClassName("MrdStatus")}
          >
            <option value="">-- Select Status --</option>
            {maritalStatusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {formErrors.MrdStatus && (
            <p className="text-red-500 text-xs italic mt-1">
              {formErrors.MrdStatus}
            </p>
          )}
        </div>

        {/* Blood Group Dropdown */}
        <div>
          <label
            htmlFor="Blood"
            className="block text-sm font-medium text-gray-700"
          >
            Blood Group <span className="text-red-500">*</span>
          </label>
          <select
            id="Blood"
            name="Blood"
            value={formData.Blood}
            onChange={(e) => handleDropdownChange("Blood", e.target.value)}
            className={getInputClassName("Blood")}
          >
            <option value="">-- Select Blood Group --</option>
            {bloodGroupOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {formErrors.Blood && (
            <p className="text-red-500 text-xs italic mt-1">
              {formErrors.Blood}
            </p>
          )}
        </div>

        {/* Gender Dropdown */}
        <div>
          <label
            htmlFor="gen"
            className="block text-sm font-medium text-gray-700"
          >
            Gender <span className="text-red-500">*</span>
          </label>
          <select
            id="gen"
            name="gen"
            value={formData.gen}
            onChange={(e) => handleDropdownChange("gen", e.target.value)}
            className={getInputClassName("gen")}
          >
            <option value="">-- Select Gender --</option>
            {genderOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {formErrors.gen && (
            <p className="text-red-500 text-xs italic mt-1">{formErrors.gen}</p>
          )}
        </div>
      </div>

      <div className="mt-6 flex justify-end gap-4">
        <button
          onClick={onClose}
          className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Save
        </button>
      </div>
    </div>
  );
};

export default MemberDetailForm;
